import api from '../services/api';
import gsap from 'gsap';
import * as PIXI from 'pixi.js';
import useCanvasStore from '../stores/canvasStore';
import { ANIMATION_FUNCTIONS, DEFAULT_CAPTION_STYLE } from './textStylePresets';
import { ensureFontLoaded } from './ensureFontLoaded';
import {
  isTypewriterCaptionStyle,
  normalizeHighlightStyle,
  sanitizeCaptionStyle,
  shouldUseWordContainer,
} from './captionStyleUtils';

const TYPEWRITER_ANIMATION = 'typewriter';

export {
  isTypewriterCaptionStyle,
  normalizeHighlightStyle,
  sanitizeCaptionStyle,
  shouldUseWordContainer,
};

const buildCaptionTextStyle = (style, wordWrapWidth) => {
  return new PIXI.TextStyle({
    fontFamily: style?.fontFamily || DEFAULT_CAPTION_STYLE.fontFamily || 'Arial',
    fontSize: style?.fontSize || DEFAULT_CAPTION_STYLE.fontSize || 36,
    fill: style?.color || '#ffffff',
    align: 'center',
    wordWrap: true,
    wordWrapWidth,
    ...(style?.stroke
      ? {
          stroke: style.stroke,
          ...(style?.strokeThickness !== undefined
            ? { strokeThickness: style.strokeThickness }
            : {}),
          ...(style?.lineJoin ? { lineJoin: style.lineJoin } : {}),
        }
      : {}),
    // Temporarily disabled (kept for later re-enable):
    // ...(style?.dropShadow
    //   ? {
    //       dropShadow: true,
    //       dropShadowColor: style.dropShadowColor || '#000000',
    //       dropShadowBlur: style.dropShadowBlur || 4,
    //       dropShadowDistance: style.dropShadowDistance ?? 0,
    //     }
    //   : {}),
  });
};

const createCaptionSprite = async ({ captionData, viewport }) => {
  const styleWithDefaults = sanitizeCaptionStyle({
    ...DEFAULT_CAPTION_STYLE,
    ...(captionData?.style || {}),
  });

  const captionFontFamily = styleWithDefaults.fontFamily || 'Arial';
  await ensureFontLoaded(captionFontFamily);

  if (shouldUseWordContainer(styleWithDefaults)) {
    const container = new PIXI.Container();
    container._isWordContainer = true;
    container._highlightColor = styleWithDefaults.highlightColor || null;
    container._baseColor = styleWithDefaults.color || '#ffffff';
    container._captionStyle = styleWithDefaults;
    container._displayMode = styleWithDefaults.displayMode || 'karaoke';
    container._highlightBg = styleWithDefaults.highlightBg || null;
    container._wordAnimation = styleWithDefaults.wordAnimation ?? null;
    container._pastPhraseColor = styleWithDefaults.pastPhraseColor || '#ffffff';
    container._activeWordBlurGraphics = null;
    container._activeWordBlurTrail = [];
    container._lastActiveWordBlurKey = null;
    container._lastActiveWordBlurAnchor = null;
    container._lastActiveWordBlurTickMs = null;
    container._activeWordBlurRenderedAnchor = null;
    return { captionSprite: container, normalizedStyle: styleWithDefaults };
  }

  const style = buildCaptionTextStyle(styleWithDefaults, viewport.worldWidth * 0.8 || 1600);

  const textSprite = new PIXI.Text({
    text: captionData?.phrases?.[0]?.text || '',
    style,
  });
  textSprite.anchor.set(0.5);

  return { captionSprite: textSprite, normalizedStyle: styleWithDefaults };
};

const attachCaptionDragHandlers = ({ captionSprite, elementId, viewport }) => {
  if (!captionSprite || !viewport || !elementId) return;

  captionSprite.eventMode = 'static';
  captionSprite.cursor = 'pointer';

  let isDragging = false;
  let dragOffset = { x: 0, y: 0 };

  const onPointerDown = (event) => {
    isDragging = true;
    const worldPosition = viewport.toWorld(event.global.x, event.global.y);
    dragOffset = {
      x: worldPosition.x - captionSprite.x,
      y: worldPosition.y - captionSprite.y,
    };

    viewport.on('pointermove', onPointerMove);

    const store = useCanvasStore.getState();
    const selectElement = store.selectElement;
    if (selectElement) {
      const selected = store.canvasElements.find((el) => el.id === elementId);
      if (selected) selectElement(selected);
    }
  };

  const onPointerMove = (event) => {
    if (!isDragging) return;

    const worldPosition = viewport.toWorld(event.global.x, event.global.y);
    let newX = worldPosition.x - dragOffset.x;
    let newY = worldPosition.y - dragOffset.y;

    const halfW = captionSprite.width / 2;
    const halfH = captionSprite.height / 2;

    newX = Math.max(halfW, Math.min(newX, viewport.worldWidth - halfW));
    newY = Math.max(halfH, Math.min(newY, viewport.worldHeight - halfH));

    captionSprite.x = newX;
    captionSprite.y = newY;
  };

  const onPointerUp = () => {
    if (!isDragging) return;

    isDragging = false;
    viewport.off('pointermove', onPointerMove);

    const latestStore = useCanvasStore.getState();
    const element = latestStore.canvasElements.find((el) => el.id === elementId);

    if (element && element.componentData?.captions) {
      const captions = element.componentData.captions;
      const newStyle = {
        ...(captions.style || {}),
        x: captionSprite.x,
        y: captionSprite.y,
      };

      latestStore.updateElement(elementId, {
        componentData: {
          ...element.componentData,
          captions: {
            ...captions,
            style: newStyle,
          },
        },
      });
    }
  };

  captionSprite.on('pointerdown', onPointerDown);
  viewport.on('pointerup', onPointerUp);
  viewport.on('pointerupoutside', onPointerUp);

  captionSprite._cleanupDrag = () => {
    captionSprite.off('pointerdown', onPointerDown);
    viewport.off('pointerup', onPointerUp);
    viewport.off('pointerupoutside', onPointerUp);
    viewport.off('pointermove', onPointerMove);
  };
};

const removeCaptionSprite = ({ captionSprite, viewport }) => {
  if (!captionSprite || captionSprite.destroyed) return;

  gsap.killTweensOf(captionSprite);
  if (captionSprite.scale) gsap.killTweensOf(captionSprite.scale);

  if (captionSprite._cleanupDrag) captionSprite._cleanupDrag();

  if (captionSprite._bgGraphics) {
    if (captionSprite._bgGraphics.parent) {
      captionSprite._bgGraphics.parent.removeChild(captionSprite._bgGraphics);
    }
    captionSprite._bgGraphics.destroy();
  }

  if (captionSprite._activeWordBlurGraphics) {
    if (captionSprite._activeWordBlurGraphics.parent) {
      captionSprite._activeWordBlurGraphics.parent.removeChild(captionSprite._activeWordBlurGraphics);
    }
    captionSprite._activeWordBlurGraphics.destroy();
  }

  if (captionSprite._captionBgGraphics) {
    if (captionSprite._captionBgGraphics.parent) {
      captionSprite._captionBgGraphics.parent.removeChild(captionSprite._captionBgGraphics);
    }
    captionSprite._captionBgGraphics.destroy();
  }

  if (captionSprite.parent) {
    captionSprite.parent.removeChild(captionSprite);
  } else if (viewport) {
    viewport.removeChild(captionSprite);
  }

  captionSprite.destroy({ children: true });
};

const GAP_THRESHOLD_MS = 1500; // Split phrases when gap between words exceeds this (e.g. music breaks)

const flushPhrase = (currentPhrase, phrases) => {
  if (currentPhrase.length > 0) {
    phrases.push({
      text: currentPhrase.map((w) => w.text).join(' '),
      start: currentPhrase[0].start,
      end: currentPhrase[currentPhrase.length - 1].end,
      words: currentPhrase.map((w) => ({
        text: w.text,
        start: w.start,
        end: w.end,
      })),
    });
  }
};

const groupTranscriptWords = (words = []) => {
  const phrases = [];
  let currentPhrase = [];

  words.forEach((word) => {
    // If there is a large time gap between the previous word and this one
    // (e.g. a music break), flush the current phrase before starting a new one.
    if (
      currentPhrase.length > 0 &&
      word.start - currentPhrase[currentPhrase.length - 1].end > GAP_THRESHOLD_MS
    ) {
      flushPhrase(currentPhrase, phrases);
      currentPhrase = [];
    }

    currentPhrase.push(word);

    if (currentPhrase.length >= 5 || word.text.match(/[.!?]$/)) {
      flushPhrase(currentPhrase, phrases);
      currentPhrase = [];
    }
  });

  flushPhrase(currentPhrase, phrases);

  return phrases;
};

export const generateCaptions = async ({ mediaUrl, element, onPropertyChange, setIsGeneratingCaption }) => {
  if (!mediaUrl || !element) return;

  setIsGeneratingCaption(true);

  try {
    const response = await api.post('/media/transcribe', { url: mediaUrl });
    const transcript = response?.data?.data?.transcript;

    if (!transcript?.words?.length) {
      setIsGeneratingCaption(false);
      return;
    }

    const phrases = groupTranscriptWords(transcript.words);

    const existingStyle = element.componentData?.captions?.style || {};
    const captionData = {
      phrases,
      style: sanitizeCaptionStyle({
        ...DEFAULT_CAPTION_STYLE,
        ...existingStyle,
        ...(existingStyle.x !== undefined ? { x: existingStyle.x } : {}),
        ...(existingStyle.y !== undefined ? { y: existingStyle.y } : {}),
      }),
    };

    onPropertyChange('componentData.captions', captionData);

    const store = useCanvasStore.getState();
    const viewport = store.viewport;

    if (!element.pixiSprite || !viewport) {
      setIsGeneratingCaption(false);
      return;
    }

    if (element.captionSprite && !element.captionSprite.destroyed) {
      removeCaptionSprite({ captionSprite: element.captionSprite, viewport });
    }

    const { captionSprite, normalizedStyle } = await createCaptionSprite({ captionData, viewport });

    captionSprite.x = normalizedStyle.x ?? viewport.worldWidth / 2;
    captionSprite.y = normalizedStyle.y ?? viewport.worldHeight * 0.85;

    attachCaptionDragHandlers({
      captionSprite,
      elementId: element.id,
      viewport,
    });

    viewport.addChild(captionSprite);

    const elementInStore = store.canvasElements.find((el) => el.id === element.id);
    if (elementInStore) {
      elementInStore.captionSprite = captionSprite;
    }

    element.captionSprite = captionSprite;
  } catch (error) {
    console.error('Error generating captions:', error);
    alert('Failed to generate captions. Please check console for details.');
  } finally {
    setIsGeneratingCaption(false);
  }
};

export const updateCaptionStyle = async ({ element, onPropertyChange, styleUpdate, isPreset = false }) => {
  const currentCaptions = element?.componentData?.captions;
  if (!currentCaptions) return;

  const currentStyle = sanitizeCaptionStyle(currentCaptions.style || {});
  const preservedPosition = {
    ...(currentStyle.x !== undefined ? { x: currentStyle.x } : {}),
    ...(currentStyle.y !== undefined ? { y: currentStyle.y } : {}),
  };

  const presetResetStyle = {
    displayMode: null,
    highlightColor: null,
    highlightBg: null,
    highlightFromPhraseIndex: 0,
    revealOpacityMode: null,
    inactiveOpacity: null,
    wordAnimation: null,
    pastPhraseColor: null,
    typewriterCursor: null,
    typewriterBackgroundColor: null,
    typewriterBackgroundPaddingX: null,
    typewriterBackgroundPaddingY: null,
    typewriterBackgroundRadius: null,
    activeWordBlur: null,
    activeWordBlurColor: null,
    activeWordBlurRadius: null,
    activeWordBlurTrailMs: null,
    activeWordBlurRevealMs: null,
    activeWordBlurOpacity: null,
    activeWordBlurOffsetX: null,
    activeWordBlurOffsetY: null,
    activeWordBlurWordAlpha: null,
    activeWordBlurXRatio: null,
    stroke: null,
    strokeThickness: undefined,
    lineJoin: null,

    // Temporarily disabled style controls.
    backgroundColor: null,
    backgroundPaddingX: DEFAULT_CAPTION_STYLE.backgroundPaddingX ?? 16,
    maxLines: DEFAULT_CAPTION_STYLE.maxLines ?? 1,
    dropShadow: false,
    dropShadowColor: DEFAULT_CAPTION_STYLE.dropShadowColor || '#000000',
    dropShadowBlur: DEFAULT_CAPTION_STYLE.dropShadowBlur ?? 4,
    dropShadowDistance: DEFAULT_CAPTION_STYLE.dropShadowDistance ?? 0,
  };

  const normalizedUpdate = normalizeHighlightStyle(styleUpdate);

  const fullPresetStyle = sanitizeCaptionStyle({
    ...DEFAULT_CAPTION_STYLE,
    ...presetResetStyle,
    ...normalizedUpdate,
    ...preservedPosition,
  });

  const mergedStyle = isPreset
    ? fullPresetStyle
    : sanitizeCaptionStyle({ ...currentStyle, ...normalizedUpdate });

  onPropertyChange('componentData.captions', {
    ...currentCaptions,
    style: mergedStyle,
  });

  const captionSprite = element.captionSprite;
  if (!captionSprite || captionSprite.destroyed) return;

  const store = useCanvasStore.getState();
  const viewport = store.viewport;

  const wasWordContainer = !!captionSprite._isWordContainer;
  const needsWordContainer = shouldUseWordContainer(mergedStyle);
  const modeChanged = wasWordContainer !== needsWordContainer;

  if (modeChanged && viewport) {
    const oldX = captionSprite.x;
    const oldY = captionSprite.y;

    removeCaptionSprite({ captionSprite, viewport });

    const { captionSprite: newSprite } = await createCaptionSprite({
      captionData: {
        phrases: currentCaptions.phrases,
        style: mergedStyle,
      },
      viewport,
    });

    newSprite.x = oldX;
    newSprite.y = oldY;

    attachCaptionDragHandlers({
      captionSprite: newSprite,
      elementId: element.id,
      viewport,
    });

    viewport.addChild(newSprite);

    element.captionSprite = newSprite;
    const elementInStore = store.canvasElements.find((el) => el.id === element.id);
    if (elementInStore) {
      elementInStore.captionSprite = newSprite;
    }

    const animationToPlay = styleUpdate.animation || (isPreset ? mergedStyle.animation : null);
    if (
      animationToPlay &&
      animationToPlay !== TYPEWRITER_ANIMATION &&
      ANIMATION_FUNCTIONS[animationToPlay]
    ) {
      ANIMATION_FUNCTIONS[animationToPlay](newSprite, true, oldX, oldY);
    }
    return;
  }

  if (captionSprite._isWordContainer) {
    captionSprite._highlightColor = mergedStyle.highlightColor || null;
    captionSprite._baseColor = mergedStyle.color || '#ffffff';
    captionSprite._displayMode = mergedStyle.displayMode || 'karaoke';
    captionSprite._highlightBg = mergedStyle.highlightBg || null;
    captionSprite._wordAnimation = mergedStyle.wordAnimation ?? null;
    captionSprite._pastPhraseColor = mergedStyle.pastPhraseColor || '#ffffff';
    captionSprite._captionStyle = mergedStyle;
    captionSprite._wordsSignature = null;

    if (!mergedStyle.highlightBg && captionSprite._bgGraphics) {
      if (captionSprite._bgGraphics.parent) {
        captionSprite._bgGraphics.parent.removeChild(captionSprite._bgGraphics);
      }
      captionSprite._bgGraphics.destroy();
      captionSprite._bgGraphics = null;
      captionSprite._lastBgKey = null;
    }

    if (!mergedStyle.activeWordBlur && captionSprite._activeWordBlurGraphics) {
      if (captionSprite._activeWordBlurGraphics.parent) {
        captionSprite._activeWordBlurGraphics.parent.removeChild(captionSprite._activeWordBlurGraphics);
      }
      captionSprite._activeWordBlurGraphics.destroy();
      captionSprite._activeWordBlurGraphics = null;
      captionSprite._activeWordBlurTrail = [];
      captionSprite._lastActiveWordBlurKey = null;
      captionSprite._lastActiveWordBlurAnchor = null;
      captionSprite._lastActiveWordBlurTickMs = null;
      captionSprite._activeWordBlurRenderedAnchor = null;
    }

    // Temporarily disabled (kept for later re-enable): caption background graphics.
    // if (!mergedStyle.backgroundColor && captionSprite._captionBgGraphics) {
    //   if (captionSprite._captionBgGraphics.parent) {
    //     captionSprite._captionBgGraphics.parent.removeChild(captionSprite._captionBgGraphics);
    //   }
    //   captionSprite._captionBgGraphics.destroy();
    //   captionSprite._captionBgGraphics = null;
    //   captionSprite._lastCaptionBgKey = null;
    // }
  } else if (captionSprite.style) {
    captionSprite.style.fontFamily = mergedStyle.fontFamily || DEFAULT_CAPTION_STYLE.fontFamily;
    captionSprite.style.fontSize = mergedStyle.fontSize || DEFAULT_CAPTION_STYLE.fontSize || 36;
    captionSprite.style.fill = mergedStyle.color || '#ffffff';
    captionSprite.style.stroke = mergedStyle.stroke;
    captionSprite.style.strokeThickness = mergedStyle.strokeThickness || 0;
    captionSprite.style.lineJoin = mergedStyle.lineJoin;

    // Temporarily disabled (kept for later re-enable): drop shadow controls.
    // captionSprite.style.dropShadow = !!mergedStyle.dropShadow;
    // captionSprite.style.dropShadowColor = mergedStyle.dropShadowColor || '#000000';
    // captionSprite.style.dropShadowBlur = mergedStyle.dropShadowBlur || 4;
    // captionSprite.style.dropShadowDistance = mergedStyle.dropShadowDistance ?? 0;
    captionSprite.style.dropShadow = false;

    if (captionSprite.style.resolution !== undefined) {
      captionSprite.style.resolution = Math.max(2, window.devicePixelRatio || 2);
    }

    // Temporarily disabled (kept for later re-enable): caption background graphics.
    // if (!mergedStyle.backgroundColor && captionSprite._captionBgGraphics) {
    //   if (captionSprite._captionBgGraphics.parent) {
    //     captionSprite._captionBgGraphics.parent.removeChild(captionSprite._captionBgGraphics);
    //   }
    //   captionSprite._captionBgGraphics.destroy();
    //   captionSprite._captionBgGraphics = null;
    // }
  }

  const animationToPlay = styleUpdate.animation || (isPreset ? mergedStyle.animation : null);
  if (animationToPlay && animationToPlay !== TYPEWRITER_ANIMATION) {
    gsap.killTweensOf(captionSprite);
    if (captionSprite.scale) gsap.killTweensOf(captionSprite.scale);

    captionSprite.alpha = 1;
    if (captionSprite.scale) captionSprite.scale.set(1, 1);

    const maxW = viewport ? viewport.worldWidth * 0.8 : 1600;
    const startX = mergedStyle.x ?? captionSprite.x;
    const startY = mergedStyle.y ?? captionSprite.y;
    const safeX = viewport ? Math.max(0, Math.min(startX, viewport.worldWidth)) : startX;
    const safeY = viewport ? Math.max(0, Math.min(startY, viewport.worldHeight)) : startY;

    captionSprite.x = safeX;
    captionSprite.y = safeY;

    if (captionSprite.style && !captionSprite.style.wordWrap) {
      captionSprite.style.wordWrap = true;
    }
    if (captionSprite.style) {
      captionSprite.style.wordWrapWidth = maxW;
    }

    if (ANIMATION_FUNCTIONS[animationToPlay]) {
      ANIMATION_FUNCTIONS[animationToPlay](captionSprite, true, safeX, safeY);
    }
  }
};

export const handleTranscriptChange = ({ newFullText, element, onPropertyChange }) => {
  const currentCaptions = element?.componentData?.captions;
  if (!currentCaptions) return;

  const previousPhrases = currentCaptions.phrases || [];
  if (previousPhrases.length === 0) return;

  const oldWords = [];
  previousPhrases.forEach((phrase) => {
    const phraseWords = phrase.text.split(/\s+/).filter(Boolean);
    const wordDuration = phraseWords.length > 0 ? (phrase.end - phrase.start) / phraseWords.length : 0;

    phraseWords.forEach((word, index) => {
      oldWords.push({
        text: word,
        start: phrase.start + index * wordDuration,
        end: phrase.start + (index + 1) * wordDuration,
      });
    });
  });

  const newWords = newFullText.split(/\s+/).filter(Boolean);

  if (!newWords.length) {
    onPropertyChange('componentData.captions', {
      ...currentCaptions,
      phrases: [],
    });
    return;
  }

  const updatedPhrases = [];
  let currentPhrase = [];

  newWords.forEach((word, index) => {
    let start;
    let end;

    if (oldWords.length === 0) {
      start = index * 500;
      end = (index + 1) * 500;
    } else if (newWords.length === oldWords.length) {
      start = oldWords[index].start;
      end = oldWords[index].end;
    } else {
      const previousPercent = index / newWords.length;
      const nextPercent = (index + 1) / newWords.length;

      const getInterpolatedTime = (percent) => {
        const exactIndex = percent * oldWords.length;
        const floorIndex = Math.floor(exactIndex);
        const ceilIndex = Math.min(oldWords.length - 1, Math.ceil(exactIndex));
        const remainder = exactIndex - floorIndex;

        const floorTime =
          floorIndex < oldWords.length
            ? oldWords[floorIndex].start
            : oldWords[oldWords.length - 1].end;
        const ceilTime =
          ceilIndex < oldWords.length
            ? oldWords[ceilIndex].start
            : oldWords[oldWords.length - 1].end;

        return floorTime + remainder * (ceilTime - floorTime);
      };

      start = getInterpolatedTime(previousPercent);
      end = getInterpolatedTime(nextPercent);
    }

    currentPhrase.push({ text: word, start, end });

    if (currentPhrase.length >= 3 || word.match(/[.!?]$/) || index === newWords.length - 1) {
      updatedPhrases.push({
        start: currentPhrase[0].start,
        end: currentPhrase[currentPhrase.length - 1].end,
        text: currentPhrase.map((phraseWord) => phraseWord.text).join(' '),
      });

      currentPhrase = [];
    }
  });

  onPropertyChange('componentData.captions', {
    ...currentCaptions,
    phrases: updatedPhrases,
  });
};
